import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HTTP_HEADERS } from '@improver/core';

import {
  AuthService,
  ImproverRefreshTokenResponse,
  LoggedUserService,
  RefreshTokenApiService,
} from '@improver/infrastructure/auth-data-layer';
import { BaseNavigationService } from '@improver/infrastructure/navigation-data-layer';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { BACKEND_ERROR_INVALID_SESSION_ID, TWO_FA_MUST_BE_PAIRED, TWO_FA_REQUIRED } from '../../improver/auth/auth.constants';
import { isInstanceRequest } from './interceptors.utils';

const makeAuthRequest = (request: HttpRequest<any>, token): HttpRequest<any> => {
  if (!token) {
    return request;
  }

  return request.clone({ headers: request.headers.set(HTTP_HEADERS.AUTHORIZATION, `Bearer ${token}`) });
};

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
  ignoredUrls: string[] = ['user/auth'];
  private refreshTokenInProgress = false;
  private refreshTokenSubject = new BehaviorSubject(null);

  constructor(
    private authService: AuthService,
    private baseNavigationService: BaseNavigationService,
    private loggedUserService: LoggedUserService,
    private refreshTokenService: RefreshTokenApiService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (!request.headers.has(HTTP_HEADERS.AUTHORIZATION)) {
      return next.handle(request);
    }

    let authReq = request.clone({
      headers: request.headers.delete(HTTP_HEADERS.AUTHORIZATION),
    });

    if (!isInstanceRequest(request.url)) {
      return next.handle(authReq);
    }

    const { token } = this.loggedUserService.getUserDataForRequest();

    if (token != null) {
      authReq = makeAuthRequest(request, token);
    }

    this.authService.setTwoFaState(null);

    return next.handle(authReq).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && !this.isIgnoredUrl(authReq.url) && error.status === 401) {
          return this.handle401Error(authReq, next, error);
        }

        return throwError(error);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler, errorResponse: HttpErrorResponse) {
    if (errorResponse.error?.message === BACKEND_ERROR_INVALID_SESSION_ID) {
      this.baseNavigationService.goToLogout().then(() => this.authService.logout());

      return throwError(() => errorResponse);
    }

    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject.pipe(
        filter((refreshToken) => refreshToken !== null),
        take(1),
        switchMap((refreshToken) => next.handle(makeAuthRequest(request, refreshToken)))
      );
    }

    this.refreshTokenInProgress = true;
    this.refreshTokenSubject.next(null);

    return this.refreshTokenService.refreshToken().pipe(
      catchError((response: HttpErrorResponse) => {
        if (response?.error?.message === TWO_FA_MUST_BE_PAIRED || response?.error?.message === TWO_FA_REQUIRED) {
          this.authService.setTwoFaState(response.error.message);
          this.baseNavigationService.goTo2fa();
        } else {
          this.baseNavigationService.goToLogout().then(() => this.authService.logout());
        }

        return throwError(() => response);
      }),
      tap((response: ImproverRefreshTokenResponse) => this.loggedUserService.setNewTokens(response)),
      switchMap(({ jwt }: ImproverRefreshTokenResponse) => {
        this.refreshTokenInProgress = false;

        this.refreshTokenSubject.next(jwt);

        return next.handle(makeAuthRequest(request, jwt));
      })
    );
  }

  private isIgnoredUrl(url: string): boolean {
    return this.ignoredUrls.some((ignoredUrl) => url.includes(ignoredUrl));
  }
}
